/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import React from 'react';
import UDAnimatedNumber from 'ud-ui/components/animated-number';
import UDCard from 'ud-ui/components/card';
import TeamLogo from 'ud-ui/components/team-logo';
import UDText from 'ud-ui/components/text';
import { AnalyticsMatchRecord, AnalyticsMatchTeam } from 'modules/analytics/domain/interfaces/tournament-analytics';
import { AnalyticsTone } from './icon-badge';
import RecordCardShell from './record-card-shell';

interface MatchRecordCardProps {
  title: string;
  icon: React.ReactNode;
  record: AnalyticsMatchRecord | null;
  /** Подпись рекорда над крупным значением: «Голов», «Разница». */
  metricLabel: string;
  tone?: AnalyticsTone;
  className?: string;
}

const TeamSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;
  text-align: center;
`;

/** Табло: светло-серая плашка на белой — цифры читаются как результат, а не как подпись. */
const Scoreboard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: ${({ theme }) => theme.radii.card};
  background: ${({ theme }) => theme.colors.SurfacePage};
`;

const TeamBlock = ({ team }: { team: AnalyticsMatchTeam }) => (
  <TeamSide>
    <TeamLogo url={team.emblemUrl ?? undefined} name={team.name} size={48} />
    <UDText variant="text-2-bold" relaxed as="div" className="!text-inkPrimary line-clamp-2 max-w-full break-words">
      {team.name}
    </UDText>
  </TeamSide>
);

const MatchRecordCard = React.memo(({ title, icon, record, metricLabel, tone = 'gold', className }: MatchRecordCardProps) => {
  if (record === null) {
    return <RecordCardShell icon={icon} title={title} tone={tone} isEmpty className={className} />;
  }

  const { team1, team2 } = record;

  return (
    <RecordCardShell
      icon={icon}
      title={title}
      tone={tone}
      metric={{ label: metricLabel, value: <UDAnimatedNumber value={record.highlightValue} /> }}
      championshipName={record.championshipName}
      matchDate={record.matchDate}
      className={className}
    >
      {/* Матч рекорда: команды по краям и счёт между ними — читается слева направо как «А 7:7 Б». */}
      <UDCard
        padding="none"
        className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 px-3 py-4"
        data-testid="record-matchup"
      >
        <TeamBlock team={team1} />
        <Scoreboard>
          <UDText variant="title-3" as="span" className="!text-inkPrimary leading-none">
            {team1.score}
          </UDText>
          <UDText variant="title-3" as="span" className="!text-inkMuted leading-none">
            :
          </UDText>
          <UDText variant="title-3" as="span" className="!text-inkPrimary leading-none">
            {team2.score}
          </UDText>
        </Scoreboard>
        <TeamBlock team={team2} />
      </UDCard>
    </RecordCardShell>
  );
});

export default MatchRecordCard;
